import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { Field, reduxForm } from 'redux-form';
import {
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  Form,
  Grid,
  Label,
  List,
  ListItem,
  ListContent,
  ListHeader,
} from 'semantic-ui-react';
import { InputField, TextAreaField } from 'components/ReduxSemanticFields';
import DayPicker from 'components/ReduxForm/day-picker';
import requisitionListsApi from 'utils/resources/requisitionLists';
import { notifyError } from 'utils/toastNotify';
import { useLocalStorage } from 'components/App/LocalStorageContext';
import { REQUEST_CREATED_KEY, RL_SELECTED_KEY, SUPPLIERS_SELECTED_KEY } from './utils';
import TenderWarehouseField from './TenderWarehouseField';

const CreateTenderForm = reduxForm({
  form: 'createTenderFromRequest',
})(({ handleSubmit, submitForm, submitRef, requisitionLists, warehouseField }) => (
  <Form onSubmit={handleSubmit(submitForm)}>
    <Grid className="po-header">
      <Grid.Row columns={2} className="main-row">
        <Grid.Column width={8}>
          <Field
            className="start_date"
            name="start_date"
            label="Start Date"
            component={DayPicker}
          />
        </Grid.Column>
        <Grid.Column width={8}>
          <Field
            className="end_date"
            name="end_date"
            label="End Date"
            component={DayPicker}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
    <Field
      className="title"
      name="title"
      label="Title"
      component={InputField}
    />
    <Field
      name="description"
      label="Description"
      className=""
      component={TextAreaField}
      rows={3}
    />
    {warehouseField}
    <hr />
    <Label as="a" fluid="true">
      Requisition Lists from this Request
    </Label>
    <List divided verticalAlign="middle">
      {requisitionLists.map((rl) => (
        <ListItem key={rl.id}>
          <ListContent>
            <ListHeader>{rl.attributes?.full_title || rl.full_title}</ListHeader>
            {`${rl.attributes?.vessel_name || rl.vessel_name} / ${rl.attributes?.equipment?.name || rl.equipment?.name || ''}`}
          </ListContent>
        </ListItem>
      ))}
    </List>
    <button type="submit" style={{ display: 'none' }} ref={submitRef} />
  </Form>
));

const CreateTenderModal = ({ open, onClose, requisitionLists, requestId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const submitRef = React.useRef();
  const [, setCreatedRequest] = useLocalStorage(REQUEST_CREATED_KEY, null);
  const [, setSelectedRL] = useLocalStorage(RL_SELECTED_KEY, []);
  const [, setSelectedSuppliers] = useLocalStorage(SUPPLIERS_SELECTED_KEY, []);
  // FIN-1267: the warehouses the lists' quotations name, and the one the tender is made for.
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [warehouseId, setWarehouseId] = useState(null);
  const listIds = requisitionLists.map((rl) => rl.id).join(',');

  useEffect(() => {
    if (!open || !listIds) return undefined;
    let live = true;
    setWarehousesLoading(true);
    requisitionListsApi.tenderWarehouses(listIds.split(','))
      .then(({ data }) => { if (live) setWarehouses(data || []); })
      .catch(() => { if (live) setWarehouses([]); })
      .finally(() => { if (live) setWarehousesLoading(false); });
    return () => { live = false; };
  }, [open, listIds]);

  const handleSubmit = useCallback(() => {
    submitRef.current?.click();
  }, []);

  const submitForm = useCallback(async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        requisition_list_ids: requisitionLists.map((rl) => rl.id),
        request_id: requestId,
        ...(warehouseId ? { warehouse_location_id: warehouseId } : {}),
      };
      const response = await requisitionListsApi.createTender(payload);
      const tenderId = response.data?.data?.id;
      if (tenderId) {
        // Clear created request and selected items from memory when tender is created
        setCreatedRequest(null);
        setSelectedRL([]);
        setSelectedSuppliers([]);
        dispatch(push(`/requisition_lists/tenders/${tenderId}`));
      }
      onClose();
    } catch (error) {
      console.error(error);
      notifyError('Something went wrong', error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  }, [requisitionLists, requestId, warehouseId, dispatch, onClose, setCreatedRequest, setSelectedRL, setSelectedSuppliers]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <ModalHeader>Create Tender from Request</ModalHeader>
      <ModalContent>
        <CreateTenderForm
          submitForm={submitForm}
          submitRef={submitRef}
          requisitionLists={requisitionLists}
          warehouseField={(
            <TenderWarehouseField warehouses={warehouses} value={warehouseId} onChange={setWarehouseId} loading={warehousesLoading} />
          )}
        />
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          positive
          onClick={handleSubmit}
          loading={loading}
          disabled={loading || requisitionLists.length === 0}
        >
          Create Tender
        </Button>
      </ModalActions>
    </Modal>
  );
};

CreateTenderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  requisitionLists: PropTypes.array.isRequired,
  requestId: PropTypes.string.isRequired,
};

export default CreateTenderModal;
